import { useEffect, useMemo, useRef, useState } from 'react'
import { Image as ImageIcon, Plus, Search } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { useToast } from '../../../components/Toast'
import { loadSession } from '../../../auth/state'
import { putFile } from '../../../utils/idbFiles'
import { fetchBlobFromUrl } from '../../../utils/upload'
import { useResolvedUrl } from '../../../utils/useResolvedUrl'
import PinDetailsModal from '../components/PinDetailsModal'
import { deletePin, formatBytes, listTags, loadPins, savePins, toggleLike, upsertPin } from '../utils/galleryStore'
import { avatarUrlForUser } from '../utils/avatar'

const PUBLIC_SEED = [
  {
    id: 'public-1',
    owner: 'public@anipin.com',
    title: 'Chamii',
    tags: ['cute', 'brand'],
    visibility: 'public',
    createdAt: Date.now() - 1000 * 60 * 60 * 24,
    updatedAt: Date.now() - 1000 * 60 * 60 * 24,
    likes: ['kid@anipin.com', 'parent@anipin.com'],
    media: {
      kind: 'image',
      url: '/chamii-logo-icon-only.png',
      thumbnailUrl: '/chamii-logo-icon-only.png',
      mime: 'image/png',
      sizeBytes: 0,
      width: 0,
      height: 0,
      durationMs: 0,
    },
  },
]

function randomId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `p_${Date.now()}_${Math.random().toString(16).slice(2)}`
  }
}

function pickUser(session) {
  const u = session?.user && typeof session.user === 'object' ? session.user : null
  const email = String(u?.email || '').trim()
  const name = String(u?.name || u?.preferred_username || '').trim()
  const picture = String(u?.picture || '').trim()
  return { id: email || name || 'guest', email, name: name || (email ? email.split('@')[0] : ''), picture }
}

function normalizeSearch(s) {
  return String(s || '').trim().toLowerCase()
}

async function loadImageMeta(url) {
  const src = String(url || '').trim()
  if (!src) return { w: 0, h: 0 }
  let objUrl = ''
  let cleaned = src
  try {
    if (cleaned.startsWith('idb:')) {
      const blob = await fetchBlobFromUrl(cleaned)
      if (blob) {
        objUrl = URL.createObjectURL(blob)
        cleaned = objUrl
      }
    }
  } catch {}
  return await new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth || 0, h: img.naturalHeight || 0 })
    img.onerror = () => resolve({ w: 0, h: 0 })
    img.src = cleaned
  })
    .finally(() => {
      if (objUrl) {
        try {
          URL.revokeObjectURL(objUrl)
        } catch {}
      }
    })
}

async function captureVideoThumbnail(videoUrl, { atSec = 0.12, maxW = 720 } = {}) {
  const url = String(videoUrl || '').trim()
  if (!url) return { blob: null, width: 0, height: 0, durationMs: 0 }

  return await new Promise((resolve) => {
    const v = document.createElement('video')
    v.muted = true
    v.playsInline = true
    v.crossOrigin = 'anonymous'
    v.src = url

    const done = (payload) => resolve(payload)
    v.onerror = () => done({ blob: null, width: 0, height: 0, durationMs: 0 })

    v.onloadedmetadata = () => {
      const w = v.videoWidth || 0
      const h = v.videoHeight || 0
      const dur = Number.isFinite(v.duration) ? v.duration : 0
      const t = Math.max(0, Math.min(atSec, dur ? Math.max(0, dur - 0.05) : atSec))
      try {
        v.currentTime = t
      } catch {
        // ignore
      }

      v.onseeked = () => {
        const scale = w && h ? Math.min(1, maxW / w) : 1
        const outW = Math.max(120, Math.round(w * scale))
        const outH = Math.max(120, Math.round(h * scale))
        const canvas = document.createElement('canvas')
        canvas.width = outW
        canvas.height = outH
        const ctx = canvas.getContext('2d')
        if (!ctx) return done({ blob: null, width: w, height: h, durationMs: dur * 1000 })
        ctx.drawImage(v, 0, 0, outW, outH)
        canvas.toBlob((blob) => done({ blob: blob || null, width: w, height: h, durationMs: dur * 1000 }), 'image/jpeg', 0.88)
      }
    }
  })
}

function PinCard({ pin, onClick }) {
  const p = pin
  const isVideo = p?.media?.kind === 'video'
  const raw = String(p?.media?.thumbnailUrl || p?.media?.url || '').trim()
  const src = useResolvedUrl(raw)
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full overflow-hidden rounded-3xl border-2 border-brand-textDark/10 bg-white dark:bg-slate-950 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.24)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.3)] transition-shadow text-left"
      title={p?.title || ''}
    >
      <div className="relative">
        {src ? (
          <img src={src} alt={p?.title || 'pin'} className="w-full h-auto object-cover block" loading="lazy" />
        ) : (
          <div className="w-full aspect-square bg-brand-bg flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-brand-text/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/60 opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="min-w-0">
            <div className="text-white text-xs font-black truncate">{p?.title || 'Untitled'}</div>
            <div className="text-white/80 text-[11px] font-bold truncate">{p?.visibility === 'public' ? 'Public' : 'Private'}</div>
          </div>
          {isVideo ? (
            <span className="px-2 py-1 rounded-full bg-white/20 border border-white/25 text-white text-[10px] font-black">VIDEO</span>
          ) : null}
        </div>
      </div>
    </button>
  )
}

export default function AppPreviewGallery() {
  const { toast } = useToast()
  const session = useMemo(() => loadSession(), [])
  const user = useMemo(() => pickUser(session), [session])

  const [scope, setScope] = useState('mine') // mine | public
  const [query, setQuery] = useState('')
  const [tag, setTag] = useState('')
  const [pins, setPins] = useState(() => loadPins())
  const [publicPins, setPublicPins] = useState(() => PUBLIC_SEED)
  const [active, setActive] = useState(null)
  const [storage, setStorage] = useState({ quota: 0, usage: 0 })

  const uploadRef = useRef(null)

  useEffect(() => {
    savePins(pins)
  }, [pins])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      try {
        const est = await navigator.storage?.estimate?.()
        if (!mounted) return
        setStorage({ quota: Number(est?.quota) || 0, usage: Number(est?.usage) || 0 })
      } catch {
        if (!mounted) return
        setStorage({ quota: 0, usage: 0 })
      }
    }
    run()
    const t = setInterval(run, 4000)
    return () => {
      mounted = false
      clearInterval(t)
    }
  }, [])

  const derivedPublic = useMemo(() => {
    const ownPublic = pins.filter((p) => p.visibility === 'public')
    const merged = [...publicPins, ...ownPublic]
    const out = []
    const seen = new Set()
    for (const p of merged) {
      if (!p?.id || seen.has(p.id)) continue
      seen.add(p.id)
      out.push(p)
    }
    return out
  }, [pins, publicPins])

  const allTags = useMemo(() => listTags(scope === 'public' ? derivedPublic : pins), [derivedPublic, pins, scope])

  const filtered = useMemo(() => {
    const list = scope === 'public' ? derivedPublic : pins
    const q = normalizeSearch(query)
    const selected = String(tag || '').trim().toLowerCase()

    return list
      .filter((p) => {
        if (selected) {
          const has = (p.tags || []).some((t) => String(t || '').toLowerCase() === selected)
          if (!has) return false
        }
        if (!q) return true
        const hay = `${p.title || ''} ${(p.tags || []).join(' ')} ${p.owner || ''}`.toLowerCase()
        return hay.includes(q)
      })
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
  }, [derivedPublic, pins, query, scope, tag])

  const stats = useMemo(() => {
    const list = pins
    let images = 0
    let gifs = 0
    let videos = 0
    let bytes = 0
    let imagesBytes = 0
    let gifsBytes = 0
    let videosBytes = 0
    for (const p of list) {
      const m = p?.media || {}
      const size = Number(m.sizeBytes) || 0
      bytes += size
      if (m.kind === 'video') {
        videos += 1
        videosBytes += size
        continue
      }
      if (String(m.mime || '').toLowerCase() === 'image/gif') {
        gifs += 1
        gifsBytes += size
      } else {
        images += 1
        imagesBytes += size
      }
    }
    return { images, gifs, videos, bytes, imagesBytes, gifsBytes, videosBytes }
  }, [pins])

  const storageLabel = useMemo(() => {
    const q = storage.quota || 0
    const u = storage.usage || 0
    const left = q > 0 ? Math.max(0, q - u) : 0
    return { quota: formatBytes(q), usage: formatBytes(u), left: formatBytes(left) }
  }, [storage.quota, storage.usage])

  const doUpload = async (file) => {
    const f = file
    if (!f) return

    toast({ kind: 'info', title: 'Uploading', message: `${f.name || 'media'} · ${formatBytes(f.size || 0)}` })

    try {
      const owner = user.email || user.id || 'guest'
      const base = {
        id: randomId(),
        owner,
        title: (f.name || 'Untitled').replace(/\.[a-z0-9]+$/i, ''),
        tags: [],
        visibility: 'private',
        likes: [],
      }

      if (f.type.startsWith('video/')) {
        const url = await putFile(f, { name: f.name || 'video', type: f.type || 'video/mp4' })
        const objUrl = URL.createObjectURL(f)
        const { blob, width, height, durationMs } = await captureVideoThumbnail(objUrl, { atSec: 0.12 })
        URL.revokeObjectURL(objUrl)
        const thumbUrl = blob ? await putFile(blob, { name: 'thumb.jpg', type: 'image/jpeg' }) : ''

        const pin = {
          ...base,
          media: { kind: 'video', url, thumbnailUrl: thumbUrl, mime: f.type || 'video/mp4', sizeBytes: f.size || 0, width, height, durationMs },
        }
        setPins((prev) => upsertPin(prev, pin))
        toast({ kind: 'success', title: 'Uploaded', message: pin.title || 'Video' })
        return
      }

      const url = await putFile(f, { name: f.name || 'image', type: f.type || 'image/jpeg' })
      const { w, h } = await loadImageMeta(url)
      const pin = {
        ...base,
        media: { kind: 'image', url, thumbnailUrl: url, mime: f.type || 'image/jpeg', sizeBytes: f.size || 0, width: w, height: h, durationMs: 0 },
      }
      setPins((prev) => upsertPin(prev, pin))
      toast({ kind: 'success', title: 'Uploaded', message: pin.title || 'Image' })
    } catch (e) {
      toast({ kind: 'error', title: 'Upload failed', message: String(e?.message || e || 'unknown error') })
    }
  }

  const onSave = (next) => {
    const id = String(next?.id || '').trim()
    if (!id) return

    const inMine = pins.some((p) => p.id === id)
    if (inMine) setPins((prev) => upsertPin(prev, next))
    else setPublicPins((prev) => upsertPin(prev, next))
    setActive(next)
  }

  const onDelete = (pinId) => {
    const id = String(pinId || '').trim()
    if (!id) return
    const inMine = pins.some((p) => p.id === id)
    if (inMine) setPins((prev) => deletePin(prev, id))
    else setPublicPins((prev) => deletePin(prev, id))
    setActive(null)
  }

  const onToggleLike = (pinId) => {
    const id = String(pinId || '').trim()
    if (!id) return
    const inMine = pins.some((p) => p.id === id)
    if (inMine) setPins((prev) => toggleLike(prev, id, user.id || user.email))
    else setPublicPins((prev) => toggleLike(prev, id, user.id || user.email))
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
          <div className="text-base font-black tracking-tight">Storage</div>
          <div className="mt-1 text-xs font-bold text-brand-text/70">
            Used {storageLabel.usage} / Total {storageLabel.quota} · Left {storageLabel.left}
          </div>
          <div className="mt-1 text-xs font-bold text-brand-text/70">
              Images {stats.images} ({formatBytes(stats.imagesBytes)}) · GIF {stats.gifs} ({formatBytes(stats.gifsBytes)}) · Videos {stats.videos} ({formatBytes(stats.videosBytes)})
          </div>
        </div>
        <div className="w-11 h-11 rounded-2xl border-2 border-brand-textDark bg-brand-yellow flex items-center justify-center shadow-[2px_2px_0px_0px_#E5BC2E]">
          <ImageIcon className="w-5 h-5" strokeWidth={2.6} />
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text/60" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search (title / tag)…"
              className="w-full rounded-3xl border-2 border-brand-textDark/20 bg-white dark:bg-slate-950 px-10 py-3 text-sm font-extrabold outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20"
            />
          </div>
          <Button variant="secondary" size="md" onClick={() => uploadRef.current?.click?.()} className="shrink-0">
            <Plus className="w-5 h-5" />
          </Button>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => doUpload(e.target.files?.[0] || null)}
          />
        </div>

        <div className="mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => {
              setScope('mine')
              setTag('')
            }}
            className={`px-3 py-1.5 rounded-full border-2 text-xs font-black shrink-0 ${
              scope === 'mine' ? 'bg-brand-yellow border-brand-textDark' : 'bg-white border-brand-textDark/10 text-brand-text/80'
            }`}
          >
            Mine
          </button>
          <button
            type="button"
            onClick={() => {
              setScope('public')
              setTag('')
            }}
            className={`px-3 py-1.5 rounded-full border-2 text-xs font-black shrink-0 ${
              scope === 'public' ? 'bg-brand-yellow border-brand-textDark' : 'bg-white border-brand-textDark/10 text-brand-text/80'
            }`}
          >
            Public
          </button>

          <div className="h-6 w-0.5 bg-brand-textDark/10 shrink-0" />

          <button
            type="button"
            onClick={() => setTag('')}
            className={`px-3 py-1.5 rounded-full border-2 text-xs font-black shrink-0 ${
              !tag ? 'bg-brand-pink text-white border-brand-textDark' : 'bg-white border-brand-textDark/10 text-brand-text/80'
            }`}
          >
            All
          </button>
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTag(t)}
              className={`px-3 py-1.5 rounded-full border-2 text-xs font-black shrink-0 ${
                String(tag || '').toLowerCase() === String(t).toLowerCase()
                  ? 'bg-brand-pink text-white border-brand-textDark'
                  : 'bg-brand-bg border-brand-textDark/10 text-brand-text/80'
              }`}
              title={t}
            >
              #{t}
            </button>
          ))}
        </div>
      </Card>

      {filtered.length ? (
        <div className="columns-1 sm:columns-2 gap-3 space-y-3">
          {filtered.map((p) => (
            <div key={p.id} className="break-inside-avoid">
              <PinCard pin={p} onClick={() => setActive(p)} />
            </div>
          ))}
        </div>
      ) : (
        <Card className="p-6 text-center">
          <div className="text-base font-black">No pins</div>
          <div className="mt-1 text-sm font-bold text-brand-text/70">Upload something or switch to Public.</div>
        </Card>
      )}

      <PinDetailsModal
        open={Boolean(active)}
        pin={active}
        onClose={() => setActive(null)}
        onSave={onSave}
        onDelete={onDelete}
        onToggleLike={onToggleLike}
        currentUser={{ id: user.id, email: user.email, picture: user.picture, avatar: avatarUrlForUser(user) }}
        allTags={allTags}
      />
    </div>
  )
}
